// apps/nos/client/src/components/Workbench/IngredientSearch/IngredientSearchHead.tsx
import { useRef } from "react";
import { cn } from "@/utils/cn";
import { Icon } from "@/components/Icon/Icon";
import { Input } from "@/components/primitives";
import { logEvent } from "@/lib/eventLogger";
import type { FamilyChip, IngredientFilters, RoleCounts } from "@/utils/types/ingredient";

type Kind = IngredientFilters["kind"];

type Props = {
  filters: IngredientFilters;
  setFilters: (next: IngredientFilters) => void;
  families: FamilyChip[];
  roleCounts: RoleCounts;
  flavorId?: string | null;
};

const dimmedChipStyle: React.CSSProperties = {
  opacity: 0.45,
  cursor: "not-allowed",
};

const subCountStyle: React.CSSProperties = {
  fontSize: 9,
  opacity: 0.7,
  marginLeft: 6,
};

export function IngredientSearchHead({
  filters,
  setFilters,
  families,
  roleCounts,
  flavorId = null,
}: Props) {
  const { q, kind, family, role } = filters;
  const set = (patch: Partial<IngredientFilters>) =>
    setFilters({ ...filters, ...patch });

  // Last search term we logged — used to skip repeat blur events when the
  // query hasn't changed. Trimmed to ignore whitespace-only diffs.
  const lastLoggedSearchRef = useRef<string>("");

  const onSearchBlur = (val: string) => {
    const trimmed = val.trim();
    if (!trimmed) return; // empty query isn't a "search"
    if (trimmed === lastLoggedSearchRef.current) return;
    lastLoggedSearchRef.current = trimmed;
    logEvent("flavor_workbench_ingredient_search_committed", {
      flavor_id: flavorId,
      search_term: trimmed,
      search_length: trimmed.length,
    });
  };

  const logFilterChange = (
    filter: "kind" | "family" | "role",
    value: string,
  ) => {
    logEvent("flavor_workbench_ingredient_filter_changed", {
      flavor_id: flavorId,
      filter,
      value: value || "all",
    });
  };

  // Per-kind tab counts. "All" = sum since kinds are disjoint per substance.
  const kindCount: Record<Kind, number> = {
    all: roleCounts.kindCounts.compound + roleCounts.kindCounts.food,
    compound: roleCounts.kindCounts.compound,
    food: roleCounts.kindCounts.food,
  };

  // Per-family chip counts. "All" = sum since compound_family_primary
  // assigns exactly one family per substance.
  const familyAllCount = Object.values(roleCounts.familyCounts).reduce(
    (acc, n) => acc + n,
    0,
  );

  const kindTabs: { id: Kind; label: string }[] = [
    { id: "all", label: "All" },
    { id: "compound", label: "Compounds" },
    { id: "food", label: "Mixtures" },
  ];

  return (
    <div className="is-head">
      <div
        className="hstack"
        style={{ justifyContent: "space-between", marginBottom: 10 }}
      >
        <div className="hstack gap-2">
          <Icon name="molecule" size={14} />
          <span className="label">Add ingredients</span>
        </div>
        <span className="mono" style={{ fontSize: 10, color: "var(--ink-4)" }}>
          {roleCounts.total} results
        </span>
      </div>

      <div className="is-search-wrap">
        <div className="is-search-icon">
          <Icon name="search" size={14} />
        </div>
        <Input
          className="is-search-input"
          placeholder="Search by name, descriptor, or CAS…"
          value={q}
          onChange={(e) => set({ q: e.target.value })}
          onBlur={(e) => onSearchBlur(e.target.value)}
        />
      </div>

      <div className="is-kind-tabs">
        {kindTabs.map((k) => {
          const n = kindCount[k.id];
          const sel = kind === k.id;
          const dimmed = n === 0 && !sel;
          return (
            <button
              key={k.id}
              type="button"
              disabled={dimmed}
              onClick={() => {
                logFilterChange("kind", k.id);
                set({ kind: k.id });
              }}
              title={`${k.label} · ${n}`}
              className={cn("is-kind-btn", sel && "active")}
              style={dimmed ? dimmedChipStyle : undefined}
            >
              <span
                className="is-kind-label"
                style={{ display: "flex", flexDirection: "column", alignItems: "center", lineHeight: 1.1 }}
              >
                <span>{k.label}</span>
                <span
                  className="mono"
                  style={{ fontSize: 9, opacity: 0.7, marginTop: 2 }}
                >
                  {n}
                </span>
              </span>
            </button>
          );
        })}
      </div>

      <div className="is-fam-row">
        {(() => {
          const dimmed = familyAllCount === 0 && family !== "";
          return (
            <button
              type="button"
              disabled={dimmed}
              onClick={() => {
                logFilterChange("family", "");
                set({ family: "" });
              }}
              className={cn("is-fam-chip", family === "" && "active")}
              style={dimmed ? dimmedChipStyle : undefined}
            >
              <span>All</span>
              <span className="mono" style={subCountStyle}>
                {familyAllCount}
              </span>
            </button>
          );
        })()}
        {families.map((f) => {
          const n = roleCounts.familyCounts[f.code] ?? 0;
          const sel = family === f.code;
          const dimmed = n === 0 && !sel;
          return (
            <button
              key={f.code}
              type="button"
              disabled={dimmed}
              onClick={() => {
                logFilterChange("family", sel ? "" : f.code);
                set({ family: sel ? "" : f.code });
              }}
              title={`${f.label} · ${n}`}
              className={cn("is-fam-chip", sel && "active")}
              style={{
                ...(dimmed ? dimmedChipStyle : {}),
                borderColor: sel ? f.color : undefined,
              }}
            >
              <span>{f.label}</span>
              <span className="mono" style={subCountStyle}>
                {n}
              </span>
            </button>
          );
        })}
      </div>

      {Object.keys(roleCounts.counts).length > 0 && (
        <div className="is-role-row">
          <div className="is-fam-row">
            <button
              type="button"
              onClick={() => {
                logFilterChange("role", "");
                set({ role: "" });
              }}
              className={cn("is-fam-chip", role === "" && "active")}
            >
              <span>All</span>
              <span className="mono" style={subCountStyle}>
                {roleCounts.roleAllCount}
              </span>
            </button>
            {Object.entries(roleCounts.counts)
              .sort(([, a], [, b]) => b - a)
              .map(([roleId, n]) => {
                const sel = role === roleId;
                const dimmed = n === 0 && !sel;
                return (
                  <button
                    key={roleId}
                    type="button"
                    disabled={dimmed}
                    onClick={() => {
                      logFilterChange("role", sel ? "" : roleId);
                      set({ role: sel ? "" : roleId });
                    }}
                    title={`${roleId} · ${n} in scope`}
                    className={cn("is-fam-chip", sel && "active")}
                    style={dimmed ? dimmedChipStyle : undefined}
                  >
                    <span>{roleId}</span>
                    <span className="mono" style={subCountStyle}>
                      {n}
                    </span>
                  </button>
                );
              })}
          </div>
        </div>
      )}
    </div>
  );
}
